import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  ExtensionComponentType,
  ICheckoutExtensions,
  MultiShipmentExtension,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { generateRandomString } from '@utils/string-utils';
import { ViewMode } from '../../../models';
import {
  createAddRecipientModalForm,
  createGiftNotesBlockForm,
  createLineItemAssignmentStatusForm,
  createRecipientsBlockForm,
  createSectionBannerForm,
  createShippingToggleForm,
  MULTI_SHIPMENT_DISPLAY_RULES,
  MultiShipmentFormGroup,
  USAGE_TOKEN_PREFIX,
  USAGE_TOKEN_RANDOM_LENGTH,
  withMultiShipmentDefaults,
} from './model';
import { createDatePickerProfilesArray } from './components/date-picker-tab/date-picker-tab.utils';

enum MultiShipmentFormTab {
  SETTINGS = 'settings',
  COMPONENTS = 'components',
  DISPLAY_CONDITIONS = 'display-conditions',
  DATE_PICKER = 'date-picker',
}

@Component({
  selector: 'multi-shipment',
  templateUrl: './multi-shipment.component.html',
  styleUrls: ['./multi-shipment.component.scss'],
  standalone: false,
})
export class MultiShipmentComponent implements OnInit {
  readonly MultiShipmentFormTab = MultiShipmentFormTab;

  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;
  formGroup: FormGroup<MultiShipmentFormGroup>;
  activeTab: MultiShipmentFormTab = MultiShipmentFormTab.SETTINGS;
  tabs: { label: string; value: MultiShipmentFormTab }[] = [
    { label: 'Settings', value: MultiShipmentFormTab.SETTINGS },
    { label: 'Components', value: MultiShipmentFormTab.COMPONENTS },
    { label: 'Date Picker Profiles', value: MultiShipmentFormTab.DATE_PICKER },
    { label: 'Display Conditions', value: MultiShipmentFormTab.DISPLAY_CONDITIONS },
  ];

  private destroyRef = inject(DestroyRef);

  constructor(
    private store: Store<CheckoutReducer.State>,
    private extensionsService: ExtensionsService,
    private rulesDynamicFormService: RulesDynamicFormService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.MULTI_SHIPMENT);

    const tabFromUrl = this.route.snapshot.queryParamMap.get('tab') as MultiShipmentFormTab;
    if (this.tabs.some((tab) => tab.value === tabFromUrl)) {
      this.activeTab = tabFromUrl;
    }

    this.initForm();

    this.loadedExtension$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.initForm(extension.template, extension.usageToken);
      }
    });

    this.extensionsService.save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.handleSave();
    });
  }

  handleSave(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const {
      usageToken,
      internalName,
      internalDescription,
      shippingToggle,
      contactSectionBanner,
      deliverySectionBanner,
      recipientsBlock,
      giftNotesBlock,
      addRecipientModal,
      lineItemAssignmentStatus,
      displayRules,
      datePickerProfiles,
    } = this.formGroup.getRawValue();
    const template = {
      internalName,
      internalDescription,
      shippingToggle,
      contactSectionBanner,
      deliverySectionBanner,
      recipientsBlock,
      giftNotesBlock,
      addRecipientModal,
      lineItemAssignmentStatus,
      displayRules,
      datePickerProfiles,
    } as unknown as MultiShipmentExtension.Template;

    const payload: Partial<ICheckoutExtensions> = {
      template,
      usageToken,
      type: ExtensionComponentType.MULTI_SHIPMENT,
    };

    if (this.loadedExtension?.id) {
      this.store.dispatch(CheckoutActions.updateExtension({ id: this.loadedExtension.id, extension: payload }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: payload }));
    }
  }

  onTabChange(tab: string): void {
    this.activeTab = tab as MultiShipmentFormTab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private initForm(loadedValues?: Partial<MultiShipmentExtension.Template>, usageToken?: string) {
    const values = withMultiShipmentDefaults(loadedValues);

    this.formGroup = new FormGroup<MultiShipmentFormGroup>({
      usageToken: new FormControl(usageToken ?? USAGE_TOKEN_PREFIX + generateRandomString(USAGE_TOKEN_RANDOM_LENGTH), {
        nonNullable: true,
      }),
      internalName: new FormControl(values.internalName, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      internalDescription: new FormControl(values.internalDescription, { nonNullable: true }),
      shippingToggle: createShippingToggleForm(values.shippingToggle),
      contactSectionBanner: createSectionBannerForm(values.contactSectionBanner),
      deliverySectionBanner: createSectionBannerForm(values.deliverySectionBanner),
      recipientsBlock: createRecipientsBlockForm(values.recipientsBlock),
      giftNotesBlock: createGiftNotesBlockForm(values.giftNotesBlock),
      addRecipientModal: createAddRecipientModalForm(values.addRecipientModal),
      lineItemAssignmentStatus: createLineItemAssignmentStatusForm(values.lineItemAssignmentStatus),
      displayRules: this.rulesDynamicFormService.initRulesFormArray(
        loadedValues?.displayRules ?? [],
        MULTI_SHIPMENT_DISPLAY_RULES,
      ) as FormArray<FormGroup>,
      datePickerProfiles: createDatePickerProfilesArray(
        loadedValues ? (loadedValues.datePickerProfiles ?? []) : undefined,
      ),
    });

    this.formGroup.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.extensionsService.setFormValid(this.formGroup.valid));

    this.extensionsService.setFormValid(this.formGroup.valid);

    this.dispatchExampleViewValues();
    this.formGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dispatchExampleViewValues());
  }

  private dispatchExampleViewValues(): void {
    const {
      internalName,
      internalDescription,
      shippingToggle,
      contactSectionBanner,
      deliverySectionBanner,
      recipientsBlock,
      giftNotesBlock,
      addRecipientModal,
      lineItemAssignmentStatus,
      displayRules,
      datePickerProfiles,
    } = this.formGroup.getRawValue();
    const template = {
      internalName,
      internalDescription,
      shippingToggle,
      contactSectionBanner,
      deliverySectionBanner,
      recipientsBlock,
      giftNotesBlock,
      addRecipientModal,
      lineItemAssignmentStatus,
      displayRules,
      datePickerProfiles,
    } as unknown as MultiShipmentExtension.Template;

    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = { [ExtensionComponentType.MULTI_SHIPMENT]: { mode, template } };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }
}
